/**
 * PageFallbackSkeleton Component
 * 
 * Skeleton loading state for page-level Suspense fallbacks.
 * Shows a generic page structure with title, subtitle, and content grid.
 * Uses normalized header sizing matching TYPOGRAPHY.headerGreeting.
 */

import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { SKELETONS, SPACING, GRIDS, LAYOUTS } from "@/design-system";
import { PageHeaderSkeleton } from "./PageHeaderSkeleton";

interface PageFallbackSkeletonProps {
  className?: string;
  /** Number of content cards to show */
  cardCount?: number;
  /** Show title and subtitle */
  showHeader?: boolean;
  /** Wrap in full-height container (for route-level loading) */
  fullPage?: boolean;
}

export function PageFallbackSkeleton({ 
  className,
  cardCount = 6,
  showHeader = true,
  fullPage = false,
}: PageFallbackSkeletonProps) {
  const content = (
    <div className={cn(SPACING.pageContainer, 'container mx-auto px-4 py-8', className)}>
      {showHeader && <PageHeaderSkeleton />}
      <div className={cn(GRIDS.featuredGridSpaced, showHeader && 'mt-6')}>
        {[...Array(cardCount)].map((_, i) => (
          <Skeleton 
            key={i} 
            className={SKELETONS.elements.image.hero} 
          />
        ))}
      </div>
    </div>
  );

  if (fullPage) {
    return <div className="min-h-screen bg-background">{content}</div>;
  }

  return content;
}
